import * as THREE from "three";
import { defineModel, type Part, type Vec3 } from "../../lib";

// Lancia Delta HF Integrale (Evoluzione, 1992) — the rally-bred all-wheel-drive
// hot hatch at true scale (1 unit = 1 metre): length 3.90, width 1.77 over the
// BLISTERED arches, height 1.38, wheelbase 2.48 (front axle +1.28, rear -1.20),
// on ~15-inch gold Speedline alloys. Nose is +Z. A small, boxy, angular
// three-door hatchback: a short flat bonnet with twin air vents, an upright
// glassy cabin, and a near-vertical hatch tail (big rear glass, wide C-pillars)
// under the Evo's prominent raised roof spoiler. The Integrale signatures: four
// muscular BODY-COLOUR blistered arches bulging proud of the body at every
// corner, rectangular headlamps flanking a small chrome Lancia shield grille,
// a slim body-colour bumper with fog lamps, wide rectangular tail lamps and a
// twin exhaust. Body panels carry the "paint" recolour slot (Lancia rally red);
// `drive` spins the wheels and `hazards` blinks the six indicators.

const PAINT = "#b81f24"; // iconic Lancia rally red — the "paint" slot default
const TRIM = "#17181b"; // gloss-black trim (grille, pillars, window surrounds)
const CLAD = "#26282c"; // matte black plastic (valances, lower intakes, sills)
const WELL = "#141518"; // dark wheel wells inside the blistered arches
const GLASS = "#2a3742"; // tinted side / rear glazing
const SCREEN = "#3a4c5e"; // windscreen (a shade lighter than the tint)
const CABIN = "#101114"; // dark interior block behind the glazing (no see-through)
const CHROME = "#ccd1d6";
const GOLD = "#c8a24a"; // Speedline gold alloy spokes and hub face
const GOLDDK = "#7d6528"; // gold rim barrel behind the spokes
const TIRE = "#1a1b1d";
const BRAKE = "#8c9196";
const LAMP = "#e8f0f4"; // headlamp glass
const AMBER = "#e8a13a";
const RED = "#a91527"; // tail lamps
const REDDARK = "#701020";
const PLATE = "#eceff2";
const SLAT = "#3a3e44"; // grille slats

// Spec-sheet skeleton (metres). Track kept just inside the blistered arches so
// the tyres fill each arch and the flares stand proud of them (a planted rally
// stance). Body core is narrow (1.60); the four blisters swell it to 1.77.
const WHEEL_R = 0.3;
const WHEEL_X = 0.74; // half track (1.48); tyre outer face at 0.86
const AXLE_F = 1.28;
const AXLE_R = -1.2; // wheelbase 2.48
const BODY_HW = 0.8; // body-core half-width (1.60); blisters reach ~0.885

function box(
  w: number, h: number, d: number, color: string,
  position: Vec3, rotation?: Vec3, extra?: Partial<Part>,
): Part {
  return { geometry: new THREE.BoxGeometry(w, h, d), color, position, rotation, ...extra };
}

function cyl(
  rTop: number, rBot: number, h: number, seg: number, color: string,
  position: Vec3, rotation?: Vec3, extra?: Partial<Part>,
): Part {
  return { geometry: new THREE.CylinderGeometry(rTop, rBot, h, seg), color, position, rotation, ...extra };
}

/** A painted body panel — carries the "paint" recolour slot. */
function paint(w: number, h: number, d: number, position: Vec3, rotation?: Vec3): Part {
  return box(w, h, d, PAINT, position, rotation, { slot: "paint" });
}

/** One ~15-inch gold Speedline alloy: tyre, brake disc, dark rim barrel, a gold
 *  hub face, five gold spokes and a chrome centre cap. Every part shares `name`
 *  so the `drive` track spins them as one node about the axle. `side` is +1 for
 *  the right (+x) wheel, -1 for the left. Outer tyre face at 0.86, just inside
 *  the blistered arch (outer ~0.885). */
function wheel(side: 1 | -1, z: number, name: string): Part[] {
  const x = side * WHEEL_X;
  const y = WHEEL_R;
  const parts: Part[] = [
    cyl(WHEEL_R, WHEEL_R, 0.24, 18, TIRE, [x, y, z], [0, 0, Math.PI / 2], { name }),
    cyl(0.13, 0.13, 0.03, 16, BRAKE, [x - side * 0.06, y, z], [0, 0, Math.PI / 2], { name }),
    cyl(0.225, 0.225, 0.06, 18, GOLDDK, [x + side * 0.05, y, z], [0, 0, Math.PI / 2], { name }),
    cyl(0.2, 0.2, 0.03, 18, GOLD, [x + side * 0.085, y, z], [0, 0, Math.PI / 2], { name }),
  ];
  for (let k = 0; k < 5; k++) {
    parts.push(
      box(0.06, 0.38, 0.05, GOLD, [x + side * 0.09, y, z], [(k * 2 * Math.PI) / 5, 0, 0], { name }),
    );
  }
  parts.push(cyl(0.055, 0.055, 0.03, 14, CHROME, [x + side * 0.1, y, z], [0, 0, Math.PI / 2], { name }));
  return parts;
}

/** One BODY-COLOUR blistered arch + its dark wheel well. The blister is a
 *  muscular swelling that stands proud of the body side: a rounded paint brow
 *  (half-torus) bulging over the top of the wheel, plus a short paint cheek
 *  ahead of and behind the wheel that swells down to the sill. A dark well disc
 *  behind the tyre reads as the arch cut. The tyre's outer face is proud of the
 *  well disc so the alloy fills the arch, and the brow/cheeks are proud of the
 *  tyre so the flare bulges past the track — the Integrale's signature box
 *  flares. */
function blister(side: 1 | -1, z: number): Part[] {
  const s = side;
  return [
    cyl(0.38, 0.38, 0.04, 20, WELL, [s * 0.8, WHEEL_R, z], [0, 0, Math.PI / 2]),
    {
      geometry: new THREE.TorusGeometry(0.42, 0.1, 8, 20, Math.PI),
      color: PAINT,
      slot: "paint",
      position: [s * 0.8, WHEEL_R, z],
      rotation: [0, Math.PI / 2, 0],
    },
    paint(0.13, 0.44, 0.11, [s * 0.835, 0.5, z + 0.38]), // front cheek
    paint(0.13, 0.44, 0.11, [s * 0.835, 0.5, z - 0.38]), // rear cheek
  ];
}

// The six hazard indicators — parts and blink tracks are generated from one
// table so the pivots (each part's own centre) can never drift from the parts.
// Front pair sits amber in the bumper; rear pair is the amber inner segment of
// each tail-lamp cluster; a side-repeater pair on the front fenders.
const INDICATORS: ReadonlyArray<{ name: string; pos: Vec3; size: Vec3 }> = [
  { name: "indicator-fl", pos: [-0.62, 0.44, 1.905], size: [0.14, 0.06, 0.03] },
  { name: "indicator-fr", pos: [0.62, 0.44, 1.905], size: [0.14, 0.06, 0.03] },
  { name: "indicator-rl", pos: [-0.4, 0.72, -1.87], size: [0.1, 0.15, 0.04] },
  { name: "indicator-rr", pos: [0.4, 0.72, -1.87], size: [0.1, 0.15, 0.04] },
  { name: "indicator-ml", pos: [-0.82, 0.66, 1.5], size: [0.03, 0.05, 0.07] },
  { name: "indicator-mr", pos: [0.82, 0.66, 1.5], size: [0.03, 0.05, 0.07] },
];

function parts(): Part[] {
  const P: Part[] = [];
  const S = [1, -1] as const;

  // -- Running gear ----------------------------------------------------------
  P.push(
    ...wheel(-1, AXLE_F, "wheel-fl"), ...wheel(1, AXLE_F, "wheel-fr"),
    ...wheel(-1, AXLE_R, "wheel-rl"), ...wheel(1, AXLE_R, "wheel-rr"),
  );
  for (const z of [AXLE_F, AXLE_R]) {
    P.push(cyl(0.05, 0.05, 1.5, 8, TRIM, [0, WHEEL_R, z], [0, 0, Math.PI / 2])); // axle
    for (const s of S) P.push(...blister(s, z));
  }
  P.push(box(1.5, 0.2, 3.4, TRIM, [0, 0.22, -0.05])); // low underbody pan, closes the profile

  // -- Body masses (paint): a boxy two-box hatch dropped onto the wheels ------
  P.push(paint(BODY_HW * 2, 0.57, 3.4, [0, 0.565, -0.05])); // main tub, sill (0.28) to shoulder (0.85)
  P.push(paint(BODY_HW * 2, 0.08, 3.0, [0, 0.85, -0.2])); // belt-line shoulder band
  P.push(paint(1.48, 0.08, 1.1, [0, 0.83, 1.08], [0.05, 0, 0])); // flat bonnet, slightly nose-down
  for (const s of S) P.push(paint(0.1, 0.09, 1.1, [s * 0.75, 0.855, 1.08], [0.05, 0, 0])); // fender tops
  P.push(paint(BODY_HW * 2, 0.5, 0.24, [0, 0.55, 1.7])); // nose block behind the fascia (0.30-0.80)
  P.push(box(1.5, 0.24, 0.3, TRIM, [0, 0.4, 1.72])); // under-nose fill (no see-through)
  P.push(box(1.5, 0.24, 0.3, TRIM, [0, 0.4, -1.72])); // under-tail fill (no see-through)

  // -- Black plastic: sills, valances -----------------------------------------
  for (const s of S) P.push(box(0.05, 0.14, 2.4, CLAD, [s * 0.795, 0.32, -0.05])); // sill, arch to arch

  // -- Greenhouse: an upright, boxy, SEALED cabin under a body-width roof -------
  // Built like the Golf's proven greenhouse: one long dark side-glass band with
  // a WIDE body-colour C-pillar covering its rear third all the way back to the
  // hatch, and a continuous roof from the windscreen top to the hatch top — so
  // there is never a flat open deck behind the cabin.
  const SCREEN_RAKE = 0.5; // fairly upright hot-hatch windscreen
  const BACK_RAKE = 0.42; // near-vertical hatch backlight
  P.push(box(1.44, 0.04, 0.12, TRIM, [0, 0.87, 0.55])); // cowl at the screen base
  // Dark interior block behind ALL the glazing, so no window is see-through.
  P.push(box(1.38, 0.36, 1.84, CABIN, [0, 1.04, -0.42])); // y 0.86-1.22, inboard of the glass
  P.push(box(1.3, 0.52, 0.04, SCREEN, [0, 1.12, 0.42], [-SCREEN_RAKE, 0, 0])); // upright windscreen, top ~1.35
  for (const s of S) P.push(paint(0.1, 0.5, 0.1, [s * 0.65, 1.12, 0.42], [-SCREEN_RAKE, 0, 0])); // A-pillars, tuck under roof
  // Roof inboard of the wide blistered body (half 0.75 < body 0.80) so the body
  // reads wider than the cabin (the Integrale look) and the side glass tucks
  // JUST under the roof edge — visible in profile, never winged out as "ears".
  P.push(paint(1.5, 0.06, 1.55, [0, 1.35, -0.475])); // roof, front 0.30 -> rear -1.25, top 1.38
  for (const s of S) {
    // One long sealed side-glass band inboard of the roof (outer 0.74 < roof
    // 0.75), a black B-pillar, and a WIDE body-colour C-pillar covering its
    // rear third back to the hatch pillars so the rear quarter reads solid.
    P.push(box(0.04, 0.46, 1.56, GLASS, [s * 0.72, 1.11, -0.42], [0, 0, s * 0.03])); // side glass band, y 0.88-1.34
    P.push(box(0.05, 0.46, 0.06, TRIM, [s * 0.725, 1.11, -0.55], [0, 0, s * 0.03])); // B-pillar
    P.push(paint(0.12, 0.48, 0.5, [s * 0.7, 1.11, -1.18], [0, 0, s * 0.03])); // wide C-pillar, z -0.93..-1.43
    P.push(box(0.02, 0.04, 1.86, CHROME, [s * 0.76, 0.88, -0.42])); // belt-line window surround
  }

  // -- Rear: near-vertical hatch drops from the roof to a SOLID tailgate --------
  // The roof runs back over the rear seats then drops as the hatch glass to the
  // tailgate — roof top (z -1.25) -> hatch glass top (-1.25) -> hatch base
  // (-1.5) -> solid rear end. No flat open deck anywhere behind the cabin.
  P.push(box(1.3, 0.5, 0.04, GLASS, [0, 1.12, -1.35], [BACK_RAKE, 0, 0])); // big hatch window
  for (const s of S) P.push(paint(0.13, 0.5, 0.06, [s * 0.64, 1.12, -1.35], [BACK_RAKE, 0, 0])); // hatch pillars
  P.push(box(1.34, 0.06, 0.09, TRIM, [0, 0.9, -1.5])); // black hatch-glass base surround
  P.push(paint(1.58, 0.46, 0.36, [0, 0.67, -1.63])); // solid tailgate / rear end (y 0.44-0.90, z -1.45..-1.81)
  // The Evo's prominent raised roof-edge spoiler on two black end plates.
  for (const s of S) P.push(box(0.06, 0.14, 0.16, TRIM, [s * 0.62, 1.44, -1.26])); // spoiler uprights
  P.push(paint(1.5, 0.07, 0.3, [0, 1.5, -1.28])); // raised spoiler blade across the top of the hatch
  P.push(box(0.4, 0.03, 0.02, REDDARK, [0, 1.47, -1.42])); // high brake light on the spoiler

  // -- Front fascia: rectangular headlamps + small Lancia shield grille --------
  for (const s of S) {
    P.push(box(0.54, 0.2, 0.05, TRIM, [s * 0.52, 0.66, 1.82])); // headlamp housing
    P.push(box(0.48, 0.15, 0.04, LAMP, [s * 0.52, 0.66, 1.845])); // rectangular headlamp lens
    P.push(box(0.02, 0.15, 0.05, TRIM, [s * 0.52, 0.66, 1.85])); // twin-lamp divider
  }
  P.push(box(0.4, 0.09, 0.05, TRIM, [0, 0.74, 1.82])); // small grille surround (high, between the lamps)
  for (const dx of [-0.11, 0, 0.11]) P.push(box(0.02, 0.07, 0.02, SLAT, [dx, 0.74, 1.85])); // grille bars
  P.push(cyl(0.05, 0.05, 0.03, 6, CHROME, [0.08, 0.74, 1.86], [Math.PI / 2, 0, 0])); // offset Lancia shield badge
  // Slim body-colour bumper with a dark lower intake, fog lamps and a plate.
  P.push(paint(1.56, 0.2, 0.14, [0, 0.44, 1.86])); // slim front bumper
  P.push(box(1.0, 0.13, 0.06, CLAD, [0, 0.4, 1.92])); // lower intake
  for (const s of S) P.push(cyl(0.055, 0.055, 0.03, 12, LAMP, [s * 0.42, 0.4, 1.93], [Math.PI / 2, 0, 0])); // fog lamps
  P.push(box(0.42, 0.11, 0.02, PLATE, [0, 0.55, 1.94])); // front plate

  // -- Rear fascia: wide rectangular tail lamps + badge + twin exhaust ---------
  for (const s of S) {
    P.push(box(0.42, 0.2, 0.05, TRIM, [s * 0.53, 0.72, -1.81])); // tail-lamp backing
    P.push(box(0.38, 0.16, 0.06, RED, [s * 0.53, 0.72, -1.83])); // wide rectangular tail lamp
    P.push(box(0.06, 0.05, 0.03, REDDARK, [s * 0.68, 0.52, -1.83])); // low reflector
  }
  P.push(cyl(0.05, 0.05, 0.02, 6, CHROME, [0, 0.78, -1.82], [Math.PI / 2, 0, 0])); // rear Lancia shield badge
  P.push(box(0.44, 0.12, 0.02, PLATE, [0, 0.56, -1.83])); // rear plate
  P.push(paint(1.56, 0.2, 0.14, [0, 0.44, -1.82])); // slim rear bumper
  P.push(box(1.2, 0.12, 0.06, CLAD, [0, 0.34, -1.88])); // rear lower valance
  for (const dx of [-0.18, 0.18]) {
    P.push(cyl(0.045, 0.045, 0.1, 12, CHROME, [dx, 0.3, -1.9], [Math.PI / 2, 0, 0])); // twin exhaust tips
  }

  // -- Bonnet vents and furniture ---------------------------------------------
  for (const dx of [-0.3, 0.3]) P.push(box(0.22, 0.025, 0.34, TRIM, [dx, 0.87, 1.08], [0.05, 0, 0])); // twin bonnet vents
  for (const s of S) {
    P.push(box(0.05, 0.025, 0.03, TRIM, [s * 0.81, 1.0, 0.32])); // mirror arm
    P.push(paint(0.09, 0.08, 0.11, [s * 0.86, 1.01, 0.3])); // body-colour mirror cap
    P.push(box(0.035, 0.06, 0.012, CHROME, [s * 0.86, 1.01, 0.248])); // mirror glass
    P.push(box(0.02, 0.03, 0.13, CHROME, [s * 0.805, 0.72, -0.35])); // door handle (three-door)
    P.push(box(0.3, 0.012, 0.025, TRIM, [s * 0.22, 0.94, 0.52], [-SCREEN_RAKE, 0, 0])); // wiper
  }
  for (const ind of INDICATORS) {
    P.push(box(ind.size[0], ind.size[1], ind.size[2], AMBER, ind.pos, undefined, { name: ind.name }));
  }

  return P;
}

// One wheel revolution per cycle; positive x-rotation rolls the car toward +Z.
const WHEEL_PIVOTS: ReadonlyArray<{ name: string; pivot: Vec3 }> = [
  { name: "wheel-fl", pivot: [-WHEEL_X, WHEEL_R, AXLE_F] },
  { name: "wheel-fr", pivot: [WHEEL_X, WHEEL_R, AXLE_F] },
  { name: "wheel-rl", pivot: [-WHEEL_X, WHEEL_R, AXLE_R] },
  { name: "wheel-rr", pivot: [WHEEL_X, WHEEL_R, AXLE_R] },
];

export const lanciaDeltaIntegrale = defineModel({
  id: "lancia-delta-integrale",
  name: "Lancia Delta Integrale",
  batchId: "cars",
  tags: ["car", "lancia", "delta", "integrale", "hatchback", "rally", "classic"],
  summary: "A 1992 Lancia Delta HF Integrale Evoluzione rally hatch at true scale, in Lancia rally red.",
  description:
    "A Lancia Delta HF Integrale (Evoluzione, 1992) at real 1 unit = 1 metre " +
    "scale (about 3.90 long, 1.77 wide over the blistered arches, 1.38 tall, " +
    "2.48 wheelbase, on gold Speedline alloys). A small, boxy, angular " +
    "three-door hatchback: a short flat bonnet with twin air vents, an upright " +
    "glassy cabin, and a near-vertical hatch tail with a big rear window and " +
    "wide C-pillars under the Evo's prominent raised roof spoiler. The " +
    "Integrale signatures are all present: four muscular body-colour blistered " +
    "arches bulging proud of the body at every corner, rectangular headlamps " +
    "flanking a small chrome Lancia shield grille, a slim body-colour bumper " +
    "with fog lamps, black sills, wide rectangular tail lamps and a twin " +
    "exhaust. Body panels carry the 'paint' recolour slot (iconic Lancia rally " +
    "red); `drive` spins the wheels and `hazards` blinks the six indicators.",
  author: "Fable 5",
  footprint: 2.1,
  parts,
  animations: [
    {
      id: "drive",
      label: "Drive",
      duration: 1,
      tracks: WHEEL_PIVOTS.map(({ name, pivot }) => ({
        target: name,
        pivot,
        rotation: [
          { t: 0, value: [0, 0, 0] as Vec3 },
          { t: 0.5, value: [Math.PI, 0, 0] as Vec3 },
          { t: 1, value: [2 * Math.PI, 0, 0] as Vec3 },
        ],
      })),
    },
    {
      id: "hazards",
      label: "Hazard lights",
      duration: 1,
      tracks: INDICATORS.map(({ name, pos }) => ({
        target: name,
        pivot: pos,
        scale: [
          { t: 0, value: 1 },
          { t: 0.42, value: 1 },
          { t: 0.5, value: 0.02 },
          { t: 0.92, value: 0.02 },
          { t: 1, value: 1 },
        ],
      })),
    },
  ],
});
